import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// ====== Import schemas from generated ======
const {
  SupplierFirestoreResponse,
  CreateSupplierDto,
  UpdateSupplierDto,
  DeleteResponseDto,
} = schemas;

// ====== Comparison Schema (not in generated) ======
export const SupplierComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: SupplierFirestoreResponse.nullable(),
}).passthrough();

// ====== Re-export schemas for external use ======
export const SupplierResponseSchema = SupplierFirestoreResponse;
export const CreateSupplierSchema = CreateSupplierDto;
export const UpdateSupplierSchema = UpdateSupplierDto;

// ====== Type Exports ======
export type SupplierResponse = z.infer<typeof SupplierFirestoreResponse>;
export type CreateSupplier = z.infer<typeof CreateSupplierDto>;
export type UpdateSupplier = z.infer<typeof UpdateSupplierDto>;
export type SupplierComparisonResult = z.infer<typeof SupplierComparisonResultSchema>;

/**
 * Create suppliers API factory
 */
export function createSuppliersApi(client: TypedApiClient) {
  return {
    /**
     * Get all suppliers
     */
    getSuppliers: (params?: {
      search?: string;
    }): Promise<SupplierResponse[]> => {
      return client.get('/suppliers', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(SupplierFirestoreResponse),
      });
    },

    /**
     * Create a new supplier
     */
    createSupplier: (data: CreateSupplier): Promise<SupplierResponse> => {
      return client.post('/suppliers', {
        data,
        requestSchema: CreateSupplierDto,
        responseSchema: SupplierFirestoreResponse,
      });
    },

    /**
     * Update an existing supplier
     */
    updateSupplier: (id: string, data: UpdateSupplier): Promise<SupplierResponse> => {
      return client.put(apiPath`/suppliers/${id}`, {
        data,
        requestSchema: UpdateSupplierDto,
        responseSchema: SupplierFirestoreResponse,
      });
    },

    /**
     * Delete a supplier
     */
    deleteSupplier: (id: string): Promise<z.infer<typeof DeleteResponseDto>> => {
      return client.delete(apiPath`/suppliers/${id}`, {
        responseSchema: DeleteResponseDto,
      });
    },

    /**
     * Compare supplier with Firestore data
     */
    compareWithFirestore: (firestoreId: string): Promise<SupplierComparisonResult> => {
      return client.get(apiPath`/suppliers/compare/${firestoreId}`, {
        responseSchema: SupplierComparisonResultSchema,
      });
    },
  };
}
